import { CustomError } from '@core/utils';

import type { UnauthApi } from '@/api';
import { CUSTOM_SCHEME } from '@/modules/wello-app';
import type { OsType } from '@/stores/DeviceStore';

type GiftProduct = NonNullable<
  NonNullable<
    Awaited<
      ReturnType<
        (typeof UnauthApi)['get:/wello/v1/allows/affiliate/gift-products/use-tab']
      >
    >['data']
  >['context']
>['contents'][number];

export type AppGuideModalPolicy = GiftProduct['appGuideModalPolicy'];
export type AffiliateOpenMode = GiftProduct['openMode'];

type MerchantDetail = NonNullable<
  NonNullable<
    Awaited<
      ReturnType<
        (typeof UnauthApi)['get:/wello/v1/allows/affiliate/merchants/{merchantCode}']
      >
    >['data']
  >['context']
>;

export type AffiliateChannelType = MerchantDetail['channelType'];

interface AffiliateGuideCondition {
  policy: AppGuideModalPolicy | undefined;
  osType: OsType | undefined;
  isDesktop: boolean;
}

//* 기획 확정(작업지시서 v2): PC는 앱 설치 경로가 없어 모달 없이 바로 이동한다.
export const shouldOpenAffiliateGuide = ({
  policy,
  osType,
  isDesktop,
}: AffiliateGuideCondition) => {
  if (isDesktop) {
    return false;
  }

  if (policy === 'ALWAYS') {
    return true;
  }

  return policy === 'ANDROID_ONLY' && osType === 'android';
};

interface AppOnlyMerchantCondition {
  channelType: AffiliateChannelType | undefined;
  isWelloApp: boolean;
  osType: OsType | undefined;
}

//* 기획 회신 3-6 · R2: 앱 전용 몰은 웹에서 적립 경로가 없다 — 모바일 웹은 설치 유도, PC 웹은 설치할 수 없어 안내만 한다.
export const resolveAppOnlyMerchantAction = ({
  channelType,
  isWelloApp,
  osType,
}: AppOnlyMerchantCondition) => {
  if (channelType !== 'APP_ONLY' || isWelloApp) {
    return null;
  }

  return osType === 'mac' || osType === 'windows'
    ? 'APP_ONLY_NOTICE'
    : 'INSTALL';
};

const VISIT_LUCKY_TICKET_ENTRY_POINTS: readonly string[] = [
  'QUICK_MENU',
  'LUCKY_TICKET',
];

interface AffiliateVerificationNoticeCondition {
  entryPointType: string;
  isWelloApp: boolean;
  isIdentityVerificationRequired: boolean;
  lastNoticedDate: string | null;
  today: string;
}

//* 기획 회신 R1: 방문 행운권은 앱 진입만 지급되고 미인증 계정은 빠진다 — 그 사실을 하루 1번만 알린다.
export const shouldNoticeAffiliateVerification = ({
  entryPointType,
  isWelloApp,
  isIdentityVerificationRequired,
  lastNoticedDate,
  today,
}: AffiliateVerificationNoticeCondition) =>
  VISIT_LUCKY_TICKET_ENTRY_POINTS.includes(entryPointType) &&
  isWelloApp &&
  isIdentityVerificationRequired &&
  lastNoticedDate !== today;

interface AffiliateRedirect {
  redirectUrl: string;
  openMode: AffiliateOpenMode | undefined;
  isWelloApp: boolean;
}

export const resolveAffiliateRedirectHref = ({
  redirectUrl,
  openMode,
  isWelloApp,
}: AffiliateRedirect) => {
  if (!isWelloApp) {
    return redirectUrl;
  }

  if (openMode === 'IN_APP_BROWSER') {
    return `${CUSTOM_SCHEME.IN_APP_EXTERNAL_PREFIX}${redirectUrl}`;
  }

  return `${CUSTOM_SCHEME.EXTERNAL_PREFIX}${redirectUrl}`;
};

const HOT_DEAL_GONE = 30404;

export const isAffiliateHotDealGone = (error: unknown) =>
  error instanceof CustomError && error.return_code === HOT_DEAL_GONE;

//* 스토어·앱 장바구니를 거치면 클릭 토큰이 끊겨 미적립된다 — 문구는 디자인 모바일(12666:49920)·PC(12666:49894)
export const AFFILIATE_APP_STORE_NOTICE_LINES = {
  mobile: [
    '앱이 없으면 앱스토어로 이동해요',
    '앱을 새로 설치했다면 웰로로 돌아와 이 버튼을 다시 눌러야 적립돼요',
    '스토어에서 곧바로 구매하면 적립되지 않아요.',
    "앱에서 장바구니를 이용할 때도 웰로의 '확인하고 이동' 버튼으로 접속해야 적립받을 수 있어요.",
  ],
  desktop: [
    "아래 '이동하기' 버튼을 눌러 제휴몰에 접속해요",
    '해당 제휴몰에서 1일 안에 결제를 마쳐요',
    "제휴몰에서 장바구니를 이용할 때도 '이동하기' 버튼으로 접속해야 적립받을 수 있어요.",
  ],
};
